import * as React from "react";

import {
  DateTimePicker,
  DateTimeRangePicker,
} from "@/shared/foundation/primitives";

import { type DateComparator, isValueless } from "../FilterSchema";

interface DateValueInputProps {
  comparator: DateComparator;
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Value input for date-typed filters. Values are UTC ISO 8601 instants.
 */
export function DateValueInput({
  comparator,
  value,
  onChange,
}: DateValueInputProps) {
  const valueless = isValueless(comparator);
  const isBetween = comparator === "BETWEEN";

  // Drop the stale second bound when the operator narrows to a single value.
  React.useLayoutEffect(() => {
    if (!valueless && !isBetween && value.length > 1) {
      onChange(value.slice(0, 1));
    }
  }, [valueless, isBetween, value, onChange]);

  if (valueless) {
    return null;
  }

  return (
    <div data-testid="date-value-input">
      {isBetween ? (
        <DateTimeRangePicker
          value={{ start: value[0], end: value[1] }}
          onChange={(range) =>
            onChange(range === null ? [] : [range.start, range.end])
          }
        />
      ) : (
        <DateTimePicker
          label={comparator === "LESS_THAN" ? "Before" : "After"}
          value={value[0] ?? null}
          onChange={(iso) => onChange(iso === null ? [] : [iso])}
        />
      )}
    </div>
  );
}
